import React, { useEffect, useContext, useState, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { Auth } from "@aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import useLocalStorage from "../hooks/useLocalStorage";
import usePrevious from "../hooks/usePrevious";
import { isObjectTruthy, parseJwt } from "../utils";
import { truncateUserData } from "../utils";
import { useNotification } from "./NotificationProvider";
import { fetchPermissions, sendUserLastLogin } from "../api/admin";
import { fetchAppMetadata } from "../api/app";

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const aController = new AbortController();
  const { setNotification } = useNotification();

  const [user, setUser] = useState(undefined);
  const [featurePermissions, setFeaturePermissions] = useState(undefined);
  const [userChallenge, setUserChallenge] = useState(undefined);
  const [isAuthenticated, setIsAuthenticated] = useLocalStorage(
    "isAuthenticated",
    false
  );
  const [authStatus, setAuthStatus] = useState(null);
  const [authToken, setAuthToken] = useLocalStorage("authToken", null);
  const [authLoading, setAuthLoading] = useState({
    loading: false,
    silent: false,
  });
  const prevToken = usePrevious(authToken);
  const [appMetaData, setAppMetaData] = useState(null);
  const [isShowActivateMfa, setIsShowActivateMfa] = useState(null);

  const navigate = useNavigate();

  const isAdmin = useMemo(() => {
    try {
      if (user?.attributes["custom:ADMIN"]?.toLowerCase() === "true")
        return true;
      return false;
    } catch (error) {
      return false;
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const isUserAdmin = useMemo(() => {
    try {
      if (user?.attributes["custom:ADMIN:USERS"]?.toLowerCase() === "true")
        return true;
      return false;
    } catch (error) {
      return false;
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const isMediaAdmin = useMemo(() => {
    try {
      if (user?.attributes["custom:ADMIN:MEDIA"]?.toLowerCase() === "true")
        return true;
      return false;
    } catch (error) {
      console.log(error);
      return false;
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function checkUser() {
    try {
      const customToken = localStorage.getItem("sso-authToken");
      if (customToken && customToken !== 'null') {
        setAuthToken(customToken);
        const userData = parseJwt(customToken);

        setUser(truncateUserData(userData));
        setIsShowActivateMfa(false);
      } else {
        const user = await Auth.currentAuthenticatedUser({ bypassCache: true });

        if (!isObjectTruthy(user)) {
          throw new Error("Error checking user auth session.");
        }

        setUser(truncateUserData(user));
        let token = await getUserToken();
        setIsShowActivateMfa(true);
        setAuthToken(token);
      }

      const permissions = await fetchPermissions({ aController });
      const appMetadata = await fetchAppMetadata({ aController });

      if (permissions?.error) {
        signOut();
        console.log(permissions.error);
      } else if (appMetadata?.error) {
        signOut();
        console.log(appMetadata.error);
      } else {
        setFeaturePermissions(permissions);
        setAppMetaData(appMetadata);
      }
    } catch (error) {
      console.log(error);
      Sentry.captureException(error, {
        tags: { context: 'checkUser' },
        level: 'error'
      });
      signOut();
    }
  }

  async function getUserToken() {
    try {
      const session = await Auth.currentSession();

      /**
       *
        Commented Out By: Patrick Fortaleza 01-18-2023
        Reason: we use the access token to authenticate
        -----------------------------------------------------------------
        const jwt = session.getIdToken().getJwtToken();
        -----------------------------------------------------------------
      */
      const jwt = session.getIdToken().getJwtToken();
      localStorage.setItem("accessToken", session?.getAccessToken()?.getJwtToken());

      /**
       *
        Commented Out By: Patrick Fortaleza 10-05-2022
        Reason: the server should decode the token when it's sent.
        -----------------------------------------------------------------
        let base64Url = jwt.split(".")[1];
        let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        let jsonPayload = decodeURIComponent(
          window
            .atob(base64)
            .split("")
            .map(function (c) {
              return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join("")
        );
        -----------------------------------------------------------------
      */
      return jwt;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  async function signIn(username, password) {
    setAuthLoading({ loading: true, silent: false });
    try {
      const user = await Auth.signIn(username, password);
      let { challengeName } = user;

      if (challengeName === "NEW_PASSWORD_REQUIRED") {
        setAuthLoading({ loading: false, silent: false });
        setUserChallenge(user);

        setNotification({
          message: "New sign-in detected. Please update your password.",
          iconName: "info circle",
          hex: "var(--info-1)",
        });

        navigate("/newpassword", {
          state: {
            username,
            challengeParam: {
              ...user.challengeParam,
            },
          },
        });

        return;
      }

      // setUser(truncateUserData(user));
      // let token = await getUserToken();
      // setAuthToken(token);
      await checkUser();
    } catch (error) {
      console.log(error);
      Sentry.captureException(error, {
        tags: { context: 'signIn', username },
        level: 'error'
      });
      setNotification({
        message: error?.message ?? "Oops! unable to sign-in.",
        iconName: "ban",
        hex: "var(--danger-1)",
      });
    }
    setAuthLoading({ loading: false, silent: false });
  }

  /**
   *
    Commented Out By: Patrick Fortaleza 10-18-2022
    Reason: sign-up currently disabled.
    -----------------------------------------------------------------
    async function signUp(username, password, email) {
      try {
        await Auth.signUp({
          username: username,
          password: password,
          attributes: {
            email: email,
          },
        });
        navigate("/confirmation");
      } catch (error) {
        console.log("error signing up:", error);
      }
    }


    async function confirmSignUp(username, code) {
      try {
        await Auth.confirmSignUp(username, code);
        console.log("user successfully signed up!");
        navigate("/signin");
      } catch (error) {
        console.log("error confirming signing up:", error);
      }
    }

    -----------------------------------------------------------------
  */

  async function challengePassword(
    newPassword,
    confirmNewPassword,
    challengeParam
  ) {
    setAuthLoading({ loading: true, silent: false });
    try {
      if (!newPassword) throw new Error("Your password cannot be empty.");
      if (newPassword !== confirmNewPassword)
        throw new Error("Your passwords do not match.");

      await Auth.completeNewPassword(userChallenge, newPassword, {
        ...challengeParam.requiredAttributes,
      });

      setUserChallenge(undefined);
      // setUser(truncateUserData(u));
      // let token = await getUserToken();
      // setAuthToken(token);
      await checkUser();

      setNotification({
        message: "Successfully updated your password.",
        iconName: "info circle",
        hex: "var(--info-1)",
      });
    } catch (error) {
      console.log(error);
      Sentry.captureException(error, {
        tags: { context: 'challengePassword' },
        level: 'error'
      });
      setNotification({
        message: error?.message ?? "Oops! unable to sign-in.",
        iconName: "ban",
        hex: "var(--danger-1)",
      });
    }
    setAuthLoading({ loading: false, silent: false });
  }

  async function requestPasswordReset(username, hasCode) {
    setAuthLoading({ loading: true, silent: false });
    try {
      if (!username) throw new Error("Username field cannot be empty.");
      if (!hasCode) await Auth.forgotPassword(username);

      setAuthStatus("pending_password_submit");
      if (!hasCode)
        setNotification({
          message: "Please check your email for your verification code.",
          iconName: "info circle",
          hex: "var(--info-1)",
        });
    } catch (error) {
      console.log(error);
      Sentry.captureException(error, {
        tags: { context: 'requestPasswordReset', username },
        level: 'error'
      });
      setNotification({
        message: error?.message ?? "Oops! unable to send password reset.",
        iconName: "ban",
        hex: "var(--danger-1)",
      });
    }

    setAuthLoading({ loading: false, silent: false });
  }

  async function submitPasswordReset(
    username,
    code,
    password,
    confirmPassword
  ) {
    setAuthLoading({ loading: true, silent: false });
    try {
      if (!username || !code || !password)
        throw new Error("Fields cannot be empty.");

      if (password !== confirmPassword)
        throw new Error("Passwords do not match.");

      const result = await Auth.forgotPasswordSubmit(username, code, password);

      if (result !== "SUCCESS")
        throw new Error("Something went wrong during password reset.");

      setNotification({
        message: "Successfully updated your password.",
        iconName: "info circle",
        hex: "var(--info-1)",
      });

      signIn(username, password);
    } catch (error) {
      console.log(error);
      Sentry.captureException(error, {
        tags: { context: 'submitPasswordReset', username },
        level: 'error'
      });
      setNotification({
        message: error?.message ?? "Oops! unable to send password reset.",
        iconName: "ban",
        hex: "var(--danger-1)",
      });
    }

    setAuthLoading({ loading: false, silent: false });
  }

  async function signOut() {
    try {
      await Auth.signOut();
      setUser(undefined);
      setAuthToken(null);
      localStorage.setItem("sso-authToken", null);
    } catch (error) {
      console.log(error);
      Sentry.captureException(error, {
        tags: { context: 'signOut' },
        level: 'warning'
      });
      setNotification({
        message: "Oops! unable to complete your request.",
        iconName: "ban",
        hex: "var(--danger-1)",
      });
    }
  }

  useEffect(() => {
    (async () => await checkUser())();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || !isObjectTruthy(user)) setIsAuthenticated(false);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authToken) {
      setIsAuthenticated(false);
    } else {
      setIsAuthenticated(true);
    }

    if (prevToken && !authToken) {
      setNotification({
        message: "You have been signed out.",
        iconName: "info circle",
        hex: "var(--info-1)",
      });
    }
  }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAuthenticated === false) {
      signOut();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect( () => {
    if (user) {
      sendUserLastLogin({aController, id: user?.attributes?.sub });
    }
  }, [user]);

  const value = {
    user,
    featurePermissions,
    isAuthenticated,
    isAdmin,
    isUserAdmin: isAdmin || isUserAdmin, // Cascade to user admin if user is admin
    isMediaAdmin: isAdmin || isMediaAdmin, // Cascade to media admin if user is admin
    authToken,
    authLoading,
    authStatus: {
      value: authStatus,
      set: setAuthStatus,
    },
    signIn,
    challengePassword,
    requestPasswordReset,
    submitPasswordReset,
    signOut,
    metaData: appMetaData,
    setIsAuthenticated,
    checkUser,
    isShowActivateMfa,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
